import { Box, Paper, Typography, useMediaQuery } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CssGroupButton from "src/components/Button/CssGroupButton";
import { STAKING_TYPE } from "src/components/ScoringChart/Stack/config";
import StackChart from "src/components/ScoringChart/Stack/StackChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import DateGroupButton, { DATE_OPTION } from "src/components/ScoringChart/tool-utils/DateGroupButton";
import CssSelector from "src/components/Selector/CssSelector";
import MultipleSelector from "src/components/Selector/MultipleSelector";
import { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING, SUB_TYPE } from "src/config/constants";
import {
  useAnalysisOverviewChartSelector,
  useAnalysisOverviewSelector,
} from "src/context/monitoring/analysisOverviewContext";
import { useParticularPoolsSelector } from "src/context/poolsContext";
import { numberWithCommas, toFixed } from "src/services/utils";

const getOption = (len) => {
  const maxPoint = len / 9;
  return {
    chart: {
      height: 500,
    },
    legend: {
      enabled: true,
    },
    ...beautifulTooltip((value) => `$${numberWithCommas(toFixed(value, 2))}`, true),
    xAxis: {
      tickInterval: maxPoint,
      type: "datetime",
      labels: {
        enabled: true,
        format: "{value:%e.%b}",
      },
    },
  };
};

export default function TokenDomination() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const { fetchTokenDomination } = useAnalysisOverviewSelector((state) => state.fetch);
  const { token: tokenConfig, fetch } = useParticularPoolsSelector(pool);
  const { tokenDominationLoading } = useAnalysisOverviewSelector((state) => state.loading);
  const tokenDomination = useAnalysisOverviewChartSelector(MONITORING.OVERVIEW.tokenDomination, pool);
  const type = tokenDomination?.metadata?.type ?? SUB_TYPE.TVL;
  const token = tokenDomination?.metadata?.tokens ?? "";

  const [stakingType, setStakingType] = useState(STAKING_TYPE.PERCENT);
  const [selectedData, setSelectedData] = useState([]);

  const subTypeConfig = [
    { label: "Supply", onClick: () => onChangeType(SUB_TYPE.SUPPLY) },
    { label: "Borrow", onClick: () => onChangeType(SUB_TYPE.BORROW) },
    { label: "Total Value Lock", onClick: () => onChangeType(SUB_TYPE.TVL) },
  ];

  const { value, active } = useMemo(() => {
    if (type === SUB_TYPE.TVL) return { value: "Total Value Lock", active: 2 };
    else if (type === SUB_TYPE.SUPPLY) return { value: "Supply", active: 0 };
    else return { value: "Borrow", active: 1 };
  }, [type]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (tokenDomination?.active === undefined)
      fetchTokenDomination(pool, { type: SUB_TYPE.TVL, number: 360, interval: 2, token: "" }, { active: 2 });
    else fetchTokenDomination(pool, tokenDomination?.metadata, { active: tokenDomination?.active });
  }, [tokenDomination?.active, pool]);

  useEffect(() => {
    let result = [];
    if (tokenConfig && tokenDomination) {
      let _tokenAddresses = Object.keys(tokenDomination.data);
      _tokenAddresses.pop("others");
      for (var address of _tokenAddresses) {
        const _tokenConfig = tokenConfig[address];
        result.push({
          label: _tokenConfig["symbol"],
          address: address,
        });
      }
    }
    setSelectedData(result);
  }, [tokenConfig, tokenDomination]);

  const { categories, series } = useMemo(() => {
    if (tokenConfig && tokenDomination && !tokenDominationLoading) {
      let series = [];
      for (var token in tokenDomination.data) {
        let name = token;
        if (token !== "others") name = tokenConfig[token].symbol;
        series.push({
          name: name,
          data: [].concat(tokenDomination["data"][token]),
        });
      }
      return {
        categories: [].concat(tokenDomination["timestamps"]),
        series: series,
      };
    }
    return {
      categories: [],
      series: [],
    };
  }, [tokenConfig, tokenDomination, tokenDominationLoading]);

  const tokenData = useMemo(() => {
    if (tokenConfig) {
      return Object.entries(tokenConfig).map((element) => {
        return {
          label: <Typography>{element[1]["symbol"]}</Typography>,
          address: element[0],
        };
      });
    } else return [];
  }, [tokenConfig, pool]);

  function onChooseItem(event, item, selectedItems) {
    let _tokens = "";
    for (const _item of selectedItems) _tokens += `${_item["address"]},`;
    const _tokenData = _tokens.slice(0, -1);
    let tokens = "";
    if (_tokenData.length > 0) tokens = _tokens.slice(0, -1);
    let _params = tokenDomination?.metadata ? { ...tokenDomination.metadata } : {};
    _params["token"] = tokens;
    fetchTokenDomination(pool, _params, { active: tokenDomination?.active });
  }

  function onRemoveItem(event, item, selectedItems) {
    let _tokens = "";
    for (const _item of selectedItems) _tokens += `${_item["address"]},`;
    const _tokenData = _tokens.slice(0, -1);
    let tokens = "";
    if (_tokenData.length > 0) tokens = _tokens.slice(0, -1);
    let _params = tokenDomination?.metadata ? { ...tokenDomination.metadata } : {};
    _params["token"] = tokens;
    fetchTokenDomination(pool, _params, { active: tokenDomination?.active });
  }

  function onChangeType(type) {
    let _params = tokenDomination?.metadata ? { ...tokenDomination.metadata } : {};
    _params["type"] = type;
    fetchTokenDomination(pool, _params, { active: tokenDomination?.active });
  }

  return (
    <DetailMonitoringWrapper
      navigation={{ text: "overview", name: "token domination", link: `/overview-analysis/${pool}` }}
      selectLink="/overview-analysis/token-domination"
      Component={{
        Description: (
          <NoteTypography>
            The chart shows the Total Supply, Total Borrow, or Total Value Locked of the selected lending pool. The
            distribution of each token is recorded every hour and shown in percentage (%) or US dollars. You can select
            or remove tokens to see the change in their distribution.
          </NoteTypography>
        ),
      }}
    >
      <Paper>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
          {match ? (
            <CssSelector
              style={{ width: "100%" }}
              config={subTypeConfig}
              value={value}
              event={{ onChooseItem: (_, item) => item.onClick() }}
            />
          ) : (
            <CssGroupButton fullWidth={match} defaultActive={active} config={subTypeConfig} />
          )}
          <CssGroupButton
            style={match ? { margin: "0.5rem 0rem" } : {}}
            fullWidth={match}
            config={[
              { label: "Percentage(%)", onClick: () => setStakingType(STAKING_TYPE.PERCENT) },
              { label: "Value(USD)", onClick: () => setStakingType(STAKING_TYPE.NORMAL) },
            ]}
          />
          <DateGroupButton
            fullWidth={match}
            defaultActive={tokenDomination?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchTokenDomination(pool, { type, from, to, token: token }, { active });
              },
            }}
            config={DATE_OPTION.map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchTokenDomination(
                  pool,
                  { type, number: data.number, interval: data.interval, token: token },
                  { active }
                );
              },
            }))}
          />
        </Box>
        <MultipleSelector
          mb={2}
          items={tokenData}
          defaultSelectedItems={selectedData}
          event={{
            onChooseItem: (event, item, selectedItems) => onChooseItem(event, item, selectedItems),
            onRemoveItem: (event, item, selectedItems) => onRemoveItem(event, item, selectedItems),
          }}
          config={{ maxItem: 5 }}
        />
        <StackChart
          isLoading={tokenDominationLoading}
          series={series}
          categories={categories}
          option={getOption(categories.length)}
          type={stakingType}
        />
      </Paper>
    </DetailMonitoringWrapper>
  );
}
